// NicknameDialog.jsx — D7 self-service display name (SB-031; extracted from
// AppShell in SB-042 — the shell had two full business dialogs inlined). Owns its
// form state; the parent only controls `open`.
import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useT } from '../i18n/index.mjs';

export default function NicknameDialog({ open, onClose }) {
  const t = useT();
  const { account, refresh } = useAuth();
  const [nick, setNick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Re-arm the form each time the dialog opens (not on every account refresh).
  useEffect(() => {
    if (open) {
      setNick(account?.displayName || '');
      setErr('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.setProfile(nick.trim());
      await refresh();
      onClose();
    } catch (e) {
      setErr(t(errKey(e, { fallback: 'shell.nick.error' })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (busy ? null : onClose())} fullWidth maxWidth="xs">
      <DialogTitle>{t('shell.nick.title')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={t('shell.nick.label')}
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          disabled={busy}
          slotProps={{ htmlInput: { maxLength: 80 } }}
          helperText={t('shell.nick.helper')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
          }}
        />
        {err ? <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
